import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, User, ArrowLeft, Shield, X } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { AuthProvider } from "../../pt/utils/http-service";
import { ClientData } from "../../pt/utils/interfaces";
import { useAuth } from "../../context/AuthContext";
import { useHistory } from "react-router-dom";
import { useIsSuperAdmin } from "../../hooks/useIsSuperAdmin";
import { useImpersonation } from "../../context/ImpersonationContext";
import { getTrainers, getTrainerClients } from "../../api/admin";

const SuperAdminPage = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth();
  const auth = new AuthProvider(getAccessTokenSilently);
  const { isSuperAdmin, loading: adminLoading } = useIsSuperAdmin(auth);
  const { impersonating, setImpersonating } = useImpersonation();
  const { toast } = useToast();
  const history = useHistory();

  const [trainers, setTrainers] = useState<string[]>([]);
  const [trainerSearch, setTrainerSearch] = useState("");
  const [selectedTrainer, setSelectedTrainer] = useState<string | null>(null);
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin || adminLoading) return;
    setLoadingTrainers(true);
    getTrainers(auth)
      .then(setTrainers)
      .catch(() =>
        toast({
          title: "Error",
          description: "Failed to load trainers.",
          variant: "destructive",
        })
      )
      .finally(() => setLoadingTrainers(false));
  }, [isSuperAdmin, adminLoading]);

  useEffect(() => {
    if (!selectedTrainer) {
      setClients([]);
      return;
    }
    setLoadingClients(true);
    getTrainerClients(selectedTrainer, auth)
      .then(setClients)
      .catch(() =>
        toast({
          title: "Error",
          description: "Failed to load clients for trainer.",
          variant: "destructive",
        })
      )
      .finally(() => setLoadingClients(false));
  }, [selectedTrainer]);

  const handleImpersonate = (email: string) => {
    setImpersonating(email);
    toast({
      title: "Impersonating",
      description: `Now viewing as ${email}`,
    });
    history.push("/clients");
  };

  const handleStopImpersonating = () => {
    setImpersonating(null);
    setSelectedTrainer(null);
    toast({ title: "Stopped impersonating" });
  };

  const filteredTrainers = trainers.filter((t) =>
    t.toLowerCase().includes(trainerSearch.toLowerCase())
  );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="p-8 text-center">
          <p className="text-gray-600">Please log in to access this page.</p>
        </Card>
      </div>
    );
  }

  if (adminLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Checking admin access...</p>
        </div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="p-8 text-center space-y-4">
          <Shield className="w-12 h-12 text-red-400 mx-auto" />
          <h2 className="text-xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">
            You do not have superadmin permissions.
          </p>
          <Button variant="outline" onClick={() => history.push("/clients")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Clients
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => history.push("/clients")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">
              <Shield className="w-7 h-7 inline-block mr-2 text-primary" />
              Super Admin
            </h1>
          </div>
        </div>

        {/* Active impersonation banner */}
        {impersonating && (
          <Card className="p-4 bg-amber-50 border-amber-200">
            <div className="flex items-center justify-between">
              <span className="text-amber-800 font-medium">
                Currently viewing as: <strong>{impersonating}</strong>
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleStopImpersonating}
              >
                <X className="w-4 h-4 mr-1" />
                Stop
              </Button>
            </div>
          </Card>
        )}

        {/* Trainer search */}
        <Card className="p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Select a Trainer
          </h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search trainers by email..."
              value={trainerSearch}
              onChange={(e) => setTrainerSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {loadingTrainers ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : (
            <div className="grid gap-2 max-h-[40vh] overflow-y-auto">
              {filteredTrainers.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No trainers found.
                </p>
              ) : (
                filteredTrainers.map((email) => (
                  <Card
                    key={email}
                    className={`p-4 cursor-pointer transition-shadow hover:shadow-md ${
                      selectedTrainer === email
                        ? "ring-2 ring-primary bg-primary/5"
                        : ""
                    }`}
                    onClick={() => setSelectedTrainer(email)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <User className="w-5 h-5 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {email}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImpersonate(email);
                        }}
                      >
                        View as Trainer
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          )}
        </Card>

        {/* Selected trainer's clients */}
        {selectedTrainer && (
          <Card className="p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Clients of {selectedTrainer}
            </h2>

            {loadingClients ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : clients.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No clients found for this trainer.
              </p>
            ) : (
              <div className="grid gap-3 max-h-[40vh] overflow-y-auto">
                {clients.map((client) => (
                  <Card key={client.ID} className="p-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {client.firstName} {client.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">{client.email}</p>
                      </div>
                      <span className="text-xs text-gray-400 capitalize">
                        {client.gender}
                      </span>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default SuperAdminPage;
